import React, {useState} from 'react';
import {withRouter} from 'react-router-dom';
import {Button, Dialog} from '@betareader/design-system';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {push} from '../../../navigation';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import fire from '../../../config/fire';
import {deleteUser, getUserProfile} from '../../../modules/user';
import {authUtil, uxAnalyticsUtil} from '../../../utils';

const STAGE = {INITIATED: 0, ANSWERING_SURVEY: 1, COMPLETING: 2};
const reasonOptions = [
  'technical_issues', 'too_expensive', 'finished_my_beta',
  'switching_to_another_product', 'missing_features',
  'did_not_meet_expectations', 'other'
];

export const DeleteAccountModal = ({changePage, deleteUser, t}) => {
  const [stage, setStage] = useState(STAGE.INITIATED);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [open, setOpen] = useState(false);

  const close = () => {
    if (stage === STAGE.COMPLETING) return;
    setOpen(false);
    setStage(STAGE.INITIATED);
    setReason('');
    setFeedback('');
  };

  const handleDeleteUser = async () => {
    try {
      const idToken = await authUtil.getFreshIdToken();
      if (!idToken) throw new Error('Missing authentication');
      setStage(STAGE.COMPLETING);
      deleteUser({idToken, reason, feedback}, async success => {
        if (!success) {
          toast.error(t('SomethingWentWrong'));
          uxAnalyticsUtil.trackException({description: 'Failed to delete user', fatal: true});
          setStage(STAGE.ANSWERING_SURVEY);
          return;
        }
        uxAnalyticsUtil.trackEvent({category: 'User', action: 'deleted-account'});
        await fire.auth().signOut();
        changePage('/goodbye');
      });
    } catch (error) {
      toast.error(t('SomethingWentWrong'));
      setStage(STAGE.ANSWERING_SURVEY);
    }
  };

  return (
    <>
      <Button variant='danger' onClick={() => setOpen(true)}>{t('DeleteAccount')}</Button>
      <Dialog open={open} title={t('DeleteAccount')} onClose={close}
        actions={
          <>
            {stage !== STAGE.COMPLETING && (
              <Button variant='secondary' onClick={stage === STAGE.ANSWERING_SURVEY
                ? () => setStage(STAGE.INITIATED) : close}>
                {stage === STAGE.INITIATED ? t('No') : t('Back')}
              </Button>
            )}
            {stage === STAGE.INITIATED && (
              <Button variant='danger' onClick={() => setStage(STAGE.ANSWERING_SURVEY)}>
                {t('Yes')}
              </Button>
            )}
            {stage === STAGE.ANSWERING_SURVEY && (
              <Button variant='danger' onClick={handleDeleteUser}>{t('DeleteAccount')}</Button>
            )}
          </>
        }>
        {stage === STAGE.INITIATED && <p>{t('DeleteAccountConfirmation')}</p>}
        {stage === STAGE.COMPLETING && <p role='status'>{t('WaitingForConfirmation')}</p>}
        {stage === STAGE.ANSWERING_SURVEY && (
          <div style={{display: 'grid', gap: '1rem'}}>
            <fieldset>
              <legend>{t('CancelReason')}</legend>
              {reasonOptions.map(option => (
                <label key={option} style={{display: 'block', margin: '.5rem 0'}}>
                  <input type='radio' name='CancelReason' value={option}
                    checked={reason === option} onChange={() => setReason(option)} />
                  {' '}{t(`cancel_reason_${option}`)}
                </label>
              ))}
            </fieldset>
            <label className='br-field' htmlFor='delete-account-feedback'>
              <span className='br-field__label'>{t('TellUsMore')}</span>
              <textarea id='delete-account-feedback' className='br-field__input'
                value={feedback} onChange={event => setFeedback(event.target.value)} />
            </label>
          </div>
        )}
      </Dialog>
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user.user,
  userProfile: state.user.userProfile
});

const mapDispatchToProps = dispatch => bindActionCreators({
  deleteUser,
  getUserProfile,
  changePage: newPage => push(newPage)
}, dispatch);

export default withTranslation()(withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(DeleteAccountModal)));
